import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, Stack, Typography, Tabs, Tab, Button, Box, CircularProgress } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { useContent } from '../../../../context/ContentContext';

const PriceListDisplay = () => {
    const { categories = [], loading, priceList = [], setSelectedCategory } = useContent();
    const [selectedTab, setSelectedTab] = useState(0);
    const [currentPriceList, setCurrentPriceList] = useState([]);
    const [step, setStep] = useState(0);

    // Set selected category and fetch price list on category change
    useEffect(() => {
        if (categories.length > 0) {
            const initialCategory = categories[0].id;
            setSelectedCategory(initialCategory);
        }
    }, [categories, setSelectedCategory]);

    useEffect(() => {
        // Update current price list whenever the selected category or price list changes
        if (categories.length > 0 && priceList.length > 0) {
            const selectedCategory = categories[selectedTab]?.id;
            if (selectedCategory) {
                const filteredPriceList = priceList.filter(item => item.category_id === selectedCategory);
                setCurrentPriceList(filteredPriceList);
            }
        }
    }, [selectedTab, categories, priceList]);

    // Handle tab change to switch category
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        const selectedCategory = categories[newValue].id;
        setSelectedCategory(selectedCategory);
    };

    const getPriceListImage = () => {
        if (!currentPriceList || currentPriceList.length === 0) return null;
        const imageFile = step === 0 ? currentPriceList[0]?.file_1 :
            step === 5 ? currentPriceList[0]?.file_5 :
                step === 10 ? currentPriceList[0]?.file_10 : currentPriceList[0]?.file_default;
        return imageFile;
    };

    const handleStep = (increment) => {
        setStep(prevStep => Math.min(Math.max(prevStep + increment, 0), 10));
    };

    const handleDownload = () => {
        const imageUrl = getPriceListImage();
        if (imageUrl) {
            // Check if the file exists and is a valid URL
            const link = document.createElement('a');
            link.href = imageUrl;

            // Check if the link is a valid URL (this is optional but useful for validation)
            const isValidUrl = imageUrl.startsWith("https://");
            if (isValidUrl) {
                // Set the download attribute with a valid filename
                link.download = `price-list-${step}%.jpg`;  // You can adjust this name if needed
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                console.error('Invalid image URL');
            }
        } else {
            console.error('No image found for the selected step');
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box sx={{ width: '100%' }}>
            <Tabs value={selectedTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                {categories.map((category, index) => (
                    <Tab key={category.id} label={category.name} />
                ))}
            </Tabs>

            <Card sx={{ marginTop: 1 }}>
                <CardHeader title="Price List" />
                <CardContent>
                    {currentPriceList && currentPriceList.length > 0 ? (
                        <>
                            <Box sx={{ textAlign: 'center', marginBottom: 0 }}>
                                <img src={getPriceListImage()} alt="Price List" style={{ maxWidth: '100%', height: 'auto' }} />
                            </Box>

                            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                                <Button variant="outlined" onClick={() => handleStep(-5)}>-</Button>
                                <Typography variant="h6">{step}%</Typography>
                                <Button variant="outlined" onClick={() => handleStep(5)}>+</Button>
                            </Stack>

                            <Box sx={{ textAlign: 'center', marginTop: 2 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleDownload}
                                    startIcon={<DownloadIcon />}
                                >
                                    Download
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <Typography variant="body1">No price list available for this category.</Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default PriceListDisplay;
